<template>
  <div class="app-wrapper" :class="{ 'app-wrapper--login': isLoginRoute }">
    <el-container v-loading="isLoading">
      <SellingDisabled
        v-if="
          !canSellInCompliantCountry &&
          isUserLoggedIn &&
          hasUser &&
          !isAllowedToBypassCompliance
        "
      />
      <!-- Modal -->
      <Modal />

      <!-- Navbar -->
      <el-header>
        <Navbar />
      </el-header>

      <!--
        Both banners are sticky top-0 with the same styling and would collide if
        shown together. They no longer overlap by construction — the uncertified
        warning is desktop-app only (FIS-405) and isWebPosBlocked is web-POS only
        (FIS-42) — but the chain stays ordered so that if that ever changes,
        "stop selling and contact your account manager" supersedes "switch to
        the desktop app".
      -->
      <UncertifiedVersionBanner v-if="isUncertifiedFrenchVersion" />
      <WebPosBlockedBanner
        v-else-if="
          isWebPosBlocked &&
          !isLoginRoute &&
          !showCashfloatOpen &&
          !showCashfloatClose
        "
      />

      <!-- Main -->
      <el-main
        v-if="!showCashfloatOpen && !showCashfloatClose"
        class="is-flex is-align-items-stretch"
      >
        <RefreshApp v-if="isRefreshing"></RefreshApp>
        <SalesClosedBanner
          v-if="
            !hasSalesStarted && isUserLoggedIn && hasUser && posConfigExists
          "
        />
        <AppMain v-if="!isRefreshing" class="app-main" />

        <Teams v-if="showTeamsView" />
      </el-main>

      <el-main
        v-if="showCashfloatOpen"
        class="is-flex is-align-items-stretch"
        style="padding: 0px"
      >
        <NewCashFloatOpenV2 v-if="isCashbookV2" />
        <NewCashFloatOpen v-else />
      </el-main>

      <el-main
        v-if="showCashfloatClose"
        class="is-flex is-align-items-stretch"
        style="padding: 0px"
      >
        <CloseCashfloatV2 v-if="isCashbookV2" />
        <CloseCashfloat v-else />
      </el-main>

      <!-- Footer -->
      <el-footer
        v-if="isAuth && !isRefreshing && (!showCashfloatClose || isCashbookV2)"
      >
        <Footer />
      </el-footer>
    </el-container>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";

// modules
import AuthModule from "@/store/modules/auth";

// components
import { Navbar, AppMain, Footer } from "./components";
import SalesClosedBanner from "@/views/Booking/components/SalesClosedBanner.vue";
import RefreshApp from "../Booking/components/RefreshApp.vue";
import { Modal } from "@/components/index";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import UserModule from "@/store/modules/user";
import { isUserLoggedIn } from "@/utils/vue-helpers/user";
import { hasSalesStarted } from "@/utils/vue-helpers/cash-float";
import AppModule from "@/store/modules/app";
import Teams from "../Booking/components/Teams.vue";
import { BookingStep } from "@/constants/booking";
import CashFloatModule from "@/store/modules/cash-float";
import SellingDisabled from "../Booking/components/SellingDisabled.vue";
import WebPosBlockedBanner from "./components/WebPosBlockedBanner.vue";
import UncertifiedVersionBanner from "./components/UncertifiedVersionBanner.vue";
import { openLink } from "@/utils/helpers/common";

import copyIcon from "@/assets/images/svg/copy.svg";
import checkIcon from "@/assets/images/svg/white-check.svg";
import closeIcon from "@/assets/images/svg/close.svg";
import { useI18n } from "vue-i18n";
import POSConfigModule from "@/store/modules/pos-config";
import {
  canSellInCompliantCountry,
  isAllowedToBypassCompliance,
  isWebPosBlocked,
  isUncertifiedFrenchVersion,
} from "@/utils/vue-helpers/compliance";
import { isCashbookV2FlowEnabled } from "@/utils/helpers/feature-flags";

export default defineComponent({
  name: "Layout",
  components: {
    Modal,
    Navbar,
    AppMain,
    Footer,
    SalesClosedBanner,
    RefreshApp,
    Teams,
    SellingDisabled,
    WebPosBlockedBanner,
    UncertifiedVersionBanner,
  },
  setup() {
    const { t } = useI18n({});

    const isAuth = computed(() => {
      return AuthModule.getIsAuth;
    });

    const route = useRoute();

    // route-reactive so leaving /login drops the white login styling
    const isLoginRoute = computed(() => {
      return route.path === "/login";
    });

    const isLoading = computed(() => {
      return AppModule.isLoading;
    });

    let isRefreshing = computed(() => AppModule.isRefreshing);

    const hasUser = computed(() => {
      return UserModule.hasUser;
    });

    const showTeamsView = computed(() => {
      return isAuth.value && useOrderMetaStore().step === BookingStep.Teams;
    });

    const showCashfloatOpen = computed(() => {
      return CashFloatModule.isOpeningCashfloat;
    });

    const showCashfloatClose = computed(() => {
      return CashFloatModule.isClosingCashfloat;
    });

    const showCopyCheck = ref(false);
    function copyToClipboard(text: string) {
      navigator.clipboard.writeText(text).then(() => {
        showCopyCheck.value = true;
        setTimeout(() => {
          showCopyCheck.value = false;
        }, 1500);
      });
    }

    const closingSummaryDocLink =
      "https://knowledge.smeetz.com/en/articles/11424684-beta-new-pos-closure-and-receipt";

    const showReleaseBanner = computed(() => {
      return !AppModule.dismissedReleaseBannerForUserIds.includes(
        UserModule.user.id
      );
    });

    function closeReleaseBanner() {
      AppModule.addDismissedReleaseBannerForUserId(Number(UserModule.userId));
    }

    const posConfigExists = computed(() => {
      return (
        POSConfigModule.selectedConfig && !!POSConfigModule.selectedConfig.id
      );
    });

    const isCashbookV2 = computed(() => isCashbookV2FlowEnabled());

    return {
      isCashbookV2,
      hasSalesStarted,
      isUserLoggedIn,
      isAuth,
      isLoading,
      hasUser,
      isRefreshing,
      showTeamsView,
      showCashfloatOpen,
      showCashfloatClose,
      openLink,
      showCopyCheck,
      copyToClipboard,
      copyIcon,
      checkIcon,
      closingSummaryDocLink,
      closeIcon,
      showReleaseBanner,
      closeReleaseBanner,
      canSellInCompliantCountry,
      isAllowedToBypassCompliance,
      isWebPosBlocked,
      isUncertifiedFrenchVersion,
      t,
      posConfigExists,
      isLoginRoute,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.app-wrapper > .el-container {
  min-height: 100vh;
}

.app-wrapper--login > .el-container {
  padding: 0;
  background-color: #ffffff;
}

.el-header,
.el-footer {
  padding: 0;
  height: auto;
}

.el-main {
  position: relative;
}

.app-wrapper--login :deep(.el-main) {
  padding: 0;
  background-color: #ffffff;
}
.announement-banner {
  position: relative;
  width: 100%;
  // height: 50px;
  padding: 10px 50px;
  margin-top: 1px;
  border-radius: 0;
  z-index: 100;
  text-align: center;
  font-weight: 700;
  // cursor: pointer;
  background-color: #409eff;
  color: #fff;
}
.doc-link {
  text-decoration: underline;
  cursor: pointer;
}
.clipboard {
  padding: 5px;
}

.clipboard {
  width: 30px;
}

.close-btn {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  cursor: pointer;
}
.close-btn:hover {
  color: #ddd;
}
</style>
